/**
 * SISTEMA: PGLP | MONTE HERMOM
 * COMPONENTE: LaudoConsolidador_soberano
 * LOCALIZAÇÃO: /src/components/laudo/
 * VERSÃO: 2.0.0 — Defensiva
 */

import React, { useMemo } from 'react';
import { Calculator, Scale, Target, ShieldCheck, AlertCircle } from 'lucide-react';

export default function LaudoConsolidador_soberano({
    dadosImovel = {},
    dadosVistoria = {},
    dadosRegiao = {},
    onReady
}) {

    /* ---------- CÁLCULO SOBERANO NBR 14653-2 ---------- */
    const payloadFinal = useMemo(() => {
        const Vb   = parseFloat(dadosImovel?.valorBase) || 0;
        const i    = dadosVistoria?.fatorHomogeneizacao || 0;
        const Vh   = Vb * (1 + i);
        const area = dadosVistoria?.areaEquivalente || 0;

        return {
            id: `LAUDO-${Date.now()}`,
            dataBase: new Date().toLocaleDateString('pt-BR'),
            valorUnitario: Vh,
            valorTotal: Vh * area,
            indiceValorizacao: (i * 100).toFixed(2),
            metrica: { Vb, i, Vh, area },
            status: "AGUARDANDO_REVISAO"
        };
    }, [dadosImovel, dadosVistoria]);

    /* ---------- DADOS SUFICIENTES? ---------- */
    const temDados = 
        payloadFinal.metrica.Vb > 0 &&
        payloadFinal.metrica.area > 0;

    /* ---------- ESTADO VAZIO (dados ainda não chegaram) ---------- */
    if (!temDados) {
        return (
            <div className="bg-[#0A0F1E] rounded-2xl border border-white/[0.06] p-12 max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <AlertCircle size={20} className="text-amber-400" />
                    <h2 className="text-sm font-black uppercase tracking-widest text-white">
                        Consolidação pendente
                    </h2>
                </div>
                <p className="text-slate-400 text-xs uppercase tracking-widest leading-relaxed mb-8">
                    Este laudo ainda não tem os dados necessários para consolidação:
                </p>
                <ul className="space-y-3 text-[11px] uppercase tracking-widest">
                    <li className={`flex items-center gap-3 ${payloadFinal.metrica.Vb > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        Valor base do imóvel
                    </li>
                    <li className={`flex items-center gap-3 ${payloadFinal.metrica.area > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        Área equivalente da vistoria
                    </li>
                </ul>
                <p className="text-slate-600 text-[10px] uppercase tracking-widest mt-10 pt-6 border-t border-white/[0.06]">
                    Complete as abas ABERTURA, VISTORIA e AMOSTRAS
                </p>
            </div>
        );
    }

    /* ---------- TELA REAL (dados completos) ---------- */
    return (
        <div className="bg-[#0A0F1E] rounded-2xl border border-white/[0.06] p-12 max-w-4xl mx-auto">

            <div className="flex justify-between items-start mb-12">
                <div className="space-y-2">
                    <h2 className="text-2xl font-black uppercase italic tracking-tighter text-white">
                        Consolidação de Ativos
                    </h2>
                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-[0.4em]">
                        Preparação para Injeção SaaS
                    </p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.06] p-6 rounded-2xl text-center">
                    <span className="block text-[8px] font-black text-slate-500 uppercase mb-1">
                        Valor Final Calculado
                    </span>
                    <span className="text-2xl font-black text-white italic">
                        R$ {payloadFinal.valorTotal.toLocaleString('pt-BR')}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {[
                    { icon: <Target className="text-blue-400" />, label: "Status Cadastro",  val: "Homologado" },
                    { icon: <ShieldCheck className="text-emerald-400" />, label: "Vistoria Campo",  val: "Completa" },
                    { icon: <Scale className="text-amber-400" />, label: "Grau Fundam.",    val: "III (Máximo)" }
                ].map((item, idx) => (
                    <div key={idx} className="bg-white/[0.02] border border-white/[0.04] p-6 rounded-2xl flex items-center gap-4">
                        <div className="bg-white/[0.04] p-3 rounded-xl">{item.icon}</div>
                        <div>
                            <span className="block text-[9px] font-black text-slate-500 uppercase">{item.label}</span>
                            <span className="text-xs font-black text-white uppercase italic">{item.val}</span>
                        </div>
                    </div>
                ))}
            </div>

            <button
                onClick={() => typeof onReady === 'function' && onReady(payloadFinal)}
                className="w-full py-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.4em] transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-4 group"
            >
                INJETAR DADOS NA CÂMARA DE REVISÃO
                <Calculator size={20} className="group-hover:rotate-12 transition-transform" />
            </button>
        </div>
    );
}